import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  HttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client'
import { ErrorLink } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { GQL_REFRESH } from '~/gql/auth/auth'
import { env } from './env'
import { useAuth } from './store/auth'

const httpLink = new HttpLink({
  uri: `${env.api.url}/graphql`,
  credentials: 'include',
})

/** Slug d'organisation courant, lu depuis l'URL `/o/:slug` (#50). */
export function currentOrgSlug(): string {
  return window.location.pathname.match(/^\/o\/([^/]+)/)?.[1] ?? 'default'
}

// Transmet l'org courante à l'API pour les requêtes PRÉ-AUTH (branding de login,
// register). Les requêtes authentifiées ignorent ce header (l'org vient du JWT).
const orgSlugLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
    headers: { ...headers, 'x-org-slug': currentOrgSlug() },
  }))
  return forward(operation)
})

const wsLink = new GraphQLWsLink(
  createClient({
    url: `${env.api.url.replace('http', 'ws')}/graphql`,
  }),
)

const refreshClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: new HttpLink({
    uri: `${env.api.url}/graphql`,
    credentials: 'include',
  }),
})

const errorLink = new ErrorLink(({ error, operation, forward }) => {
  if (CombinedGraphQLErrors.is(error)) {
    for (const err of error.errors) {
      if (err.extensions?.code === 'UNAUTHENTICATED') {
        return new Observable((observer) => {
          (async () => {
            try {
              const { data } = await refreshClient.mutate({
                mutation: GQL_REFRESH,
                fetchPolicy: 'no-cache',
              })

              if (!data) {
                throw new Error('No data returned from refresh query')
              }

              if (data.refresh.__typename === 'CustomError') {
                throw new Error(data.refresh.message)
              }

              useAuth.getState().setPayload({
                email: data.refresh.email,
                firstName: data.refresh.firstName,
                lastName: data.refresh.lastName,
                role: data.refresh.roles,
                id: data.refresh.id,
              })

              forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              })
            } catch (error) {
              useAuth.getState().logout()
              observer.error(error)
            }
          })()
        })
      } else {
        console.error(`[Network error]: ${error}`)
      }
    }
  }
})

export const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.from([orgSlugLink, errorLink, httpLink]),
})

export const wsClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: wsLink,
})
